import { apiClient } from "../../api/client";
import {
  parseAircraftSectionDefinitionPin,
  parseAircraftSectionOccurrence,
  parseAircraftSectionPlane,
  parseAircraftSectionPoseStatePin,
  parseAircraftSectionTarget,
  parseAircraftSectionVersionPin,
  type AircraftSectionDefinitionPin,
  type AircraftSectionOccurrence,
  type AircraftSectionPlane,
  type AircraftSectionTarget,
  type AircraftSectionVersionPin,
} from "./aircraftSectionExtractionApi";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SHA256 = /^sha256:[0-9a-f]{64}$/;
export const AIRCRAFT_SECTION_LOCAL_BOOTSTRAP_PATH =
  "/thread/v1/aero/aircraft-section-local-bootstrap" as const;

export interface AircraftSectionAcceptedSceneBinding {
  readonly acceptedGeometryRevision: string;
  readonly acceptedTopologyRevision: string;
  readonly payloadRevision: string;
  readonly poseApplication: "identity-root/v1";
  readonly sourceDefinitionRevision: string;
}

export interface AircraftSectionExtractionBinding {
  readonly acceptedSceneBinding: AircraftSectionAcceptedSceneBinding;
  readonly aircraftVersionPin: AircraftSectionVersionPin;
  readonly defaultPlane: AircraftSectionPlane;
  readonly definitionPin: AircraftSectionDefinitionPin;
  readonly extractorRevision: string;
  readonly label: string;
  readonly occurrence: AircraftSectionOccurrence;
  readonly poseStatePin: AircraftSectionVersionPin & {
    readonly definitionPin: AircraftSectionDefinitionPin;
    readonly stateSchema: "loom.aircraft-pose/v1";
  };
  readonly projectId: string;
  readonly sectionArtifactLogicalId: string;
  readonly sourceGeometryPin: AircraftSectionVersionPin;
  readonly target: AircraftSectionTarget;
}

export interface AircraftSectionBindingSet {
  readonly aircraftVersionPin: AircraftSectionVersionPin;
  readonly discoveryReceiptRevision: string;
  readonly entries: readonly Omit<AircraftSectionExtractionBinding,
    "aircraftVersionPin" | "poseStatePin" | "projectId">[];
  readonly poseStatePin: AircraftSectionExtractionBinding["poseStatePin"];
  readonly projectId: string;
  readonly schema: "loom.aircraft-section-binding-set/v1";
}

type Row = Record<string, unknown>;

function row(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Row;
}

function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new Error(`${path} has an unsupported or incomplete field set`);
  }
}

function uuid(value: unknown, path: string): string {
  if (typeof value !== "string" || !UUID.test(value)) {
    throw new Error(`${path} must be a canonical lowercase UUID`);
  }
  return value;
}

function sha(value: unknown, path: string): string {
  if (typeof value !== "string" || !SHA256.test(value)) {
    throw new Error(`${path} must be a sha256 revision`);
  }
  return value;
}

function acceptedSceneBinding(value: unknown, path: string): AircraftSectionAcceptedSceneBinding {
  const item = row(value, path);
  exact(item, ["sourceDefinitionRevision", "acceptedGeometryRevision",
    "acceptedTopologyRevision", "payloadRevision", "poseApplication"], path);
  if (item.poseApplication !== "identity-root/v1") {
    throw new Error(`${path}.poseApplication is unsupported`);
  }
  return {
    sourceDefinitionRevision: sha(item.sourceDefinitionRevision, `${path}.sourceDefinitionRevision`),
    acceptedGeometryRevision: sha(item.acceptedGeometryRevision, `${path}.acceptedGeometryRevision`),
    acceptedTopologyRevision: sha(item.acceptedTopologyRevision, `${path}.acceptedTopologyRevision`),
    payloadRevision: sha(item.payloadRevision, `${path}.payloadRevision`),
    poseApplication: item.poseApplication,
  };
}

export function aircraftSectionSceneBindingsMatch(
  left: AircraftSectionAcceptedSceneBinding | null | undefined,
  right: AircraftSectionAcceptedSceneBinding | null | undefined,
): boolean {
  return Boolean(left && right && equal(left, right));
}

function equal(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => equal(item, right[index]));
  }
  if (typeof left === "object" && left !== null && typeof right === "object" && right !== null) {
    const leftRow = left as Row;
    const rightRow = right as Row;
    const keys = Object.keys(leftRow).sort();
    return keys.length === Object.keys(rightRow).length
      && keys.every((key) => Object.prototype.hasOwnProperty.call(rightRow, key)
        && equal(leftRow[key], rightRow[key]));
  }
  return left === right;
}

export function aircraftSectionBindingPath(projectId: string): string {
  return `/thread/v1/projects/${uuid(projectId, "projectId")}/aero/aircraft/section-bindings`;
}

export function parseAircraftSectionBindingSet(
  value: unknown,
  expectedProjectId: string,
): AircraftSectionBindingSet {
  const document = row(value, "aircraft section binding set");
  exact(document, ["schema", "projectId", "aircraftVersionPin", "poseStatePin",
    "entries", "discoveryReceiptRevision"], "aircraft section binding set");
  if (document.schema !== "loom.aircraft-section-binding-set/v1") {
    throw new Error("aircraft section binding schema is unsupported");
  }
  const projectId = uuid(document.projectId, "bindingSet.projectId");
  if (projectId !== uuid(expectedProjectId, "expectedProjectId")) {
    throw new Error("aircraft section binding project identity is stale");
  }
  const aircraftVersionPin = parseAircraftSectionVersionPin(
    document.aircraftVersionPin, "bindingSet.aircraftVersionPin",
  );
  const poseStatePin = parseAircraftSectionPoseStatePin(
    document.poseStatePin, "bindingSet.poseStatePin",
  );
  if (!Array.isArray(document.entries) || document.entries.length < 1 || document.entries.length > 256) {
    throw new Error("bindingSet.entries must contain between 1 and 256 bindings");
  }
  const entries = document.entries.map((value, index) => {
    const path = `bindingSet.entries[${index}]`;
    const entry = row(value, path);
    exact(entry, ["definitionPin", "occurrence", "sourceGeometryPin", "target", "acceptedSceneBinding",
      "extractorRevision", "defaultPlane", "label", "sectionArtifactLogicalId"], path);
    const definitionPin = parseAircraftSectionDefinitionPin(entry.definitionPin, `${path}.definitionPin`);
    if (!equal(definitionPin, poseStatePin.definitionPin)) {
      throw new Error(`${path}.definitionPin does not match the pinned physical pose`);
    }
    if (typeof entry.label !== "string" || entry.label !== entry.label.trim()
        || !/^[\x20-\x7e]{1,300}$/.test(entry.label)) {
      throw new Error(`${path}.label is invalid`);
    }
    const sectionArtifactLogicalId = uuid(
      entry.sectionArtifactLogicalId, `${path}.sectionArtifactLogicalId`,
    );
    const sourceGeometryPin = parseAircraftSectionVersionPin(
      entry.sourceGeometryPin, `${path}.sourceGeometryPin`,
    );
    const inputLogicalIds = [aircraftVersionPin.logicalId, definitionPin.logicalId,
      poseStatePin.logicalId, sourceGeometryPin.logicalId];
    if (inputLogicalIds.includes(sectionArtifactLogicalId)) {
      throw new Error(`${path}.sectionArtifactLogicalId collides with an input logical ID`);
    }
    const parsedTarget = parseAircraftSectionTarget(entry.target, `${path}.target`);
    const sortedFaces = [...parsedTarget.sourceFaceIds].sort((left, right) =>
      left < right ? -1 : left > right ? 1 : 0);
    if (!equal(parsedTarget.sourceFaceIds, sortedFaces)) {
      throw new Error(`${path}.target.sourceFaceIds is not in canonical ASCII order`);
    }
    return {
      definitionPin,
      acceptedSceneBinding: acceptedSceneBinding(entry.acceptedSceneBinding, `${path}.acceptedSceneBinding`),
      occurrence: parseAircraftSectionOccurrence(entry.occurrence, `${path}.occurrence`),
      sourceGeometryPin,
      target: parsedTarget,
      extractorRevision: sha(entry.extractorRevision, `${path}.extractorRevision`),
      defaultPlane: parseAircraftSectionPlane(entry.defaultPlane, `${path}.defaultPlane`),
      label: entry.label, sectionArtifactLogicalId,
    };
  });
  const outputIds = entries.map((entry) => entry.sectionArtifactLogicalId);
  if (new Set(outputIds).size !== outputIds.length) throw new Error("bindingSet.entries has duplicate output logical IDs");
  const canonicalKeys = entries.map((entry) => [entry.label, entry.target.bodyId,
    entry.target.sourceFaceIds.join("\u0000"), entry.sectionArtifactLogicalId].join("\u0001"));
  const sortedKeys = [...canonicalKeys].sort((left, right) => left < right ? -1 : left > right ? 1 : 0);
  if (!equal(canonicalKeys, sortedKeys)) throw new Error("bindingSet.entries is not in canonical ASCII order");
  return {
    schema: document.schema, projectId, aircraftVersionPin, poseStatePin, entries,
    discoveryReceiptRevision: sha(document.discoveryReceiptRevision, "bindingSet.discoveryReceiptRevision"),
  };
}

export function selectAircraftSectionBinding(set: AircraftSectionBindingSet): AircraftSectionExtractionBinding {
  // The local accepted-mesh fixture exposes both body and wing targets. Its
  // canonical wire order is label-based (Body before Wing), while the first
  // useful 2D authoring slice is the wing section. Prefer that target without
  // changing or weakening the canonical server order; generic aircraft with
  // no wing still fall back to their first authoritative target.
  const entry = set.entries.find((candidate) => candidate.target.kind === "wing")
    ?? set.entries[0]!;
  return { ...entry, projectId: set.projectId, aircraftVersionPin: set.aircraftVersionPin,
    poseStatePin: set.poseStatePin };
}

export async function requestAircraftSectionBindingSet(
  projectId: string,
  signal: AbortSignal,
): Promise<AircraftSectionBindingSet> {
  const value = await apiClient(aircraftSectionBindingPath(projectId), { method: "GET", signal });
  return parseAircraftSectionBindingSet(value, projectId);
}

export async function requestLocalAircraftSectionBootstrap(signal: AbortSignal): Promise<void> {
  // The local bootstrap response is deliberately discarded. Only a subsequent
  // strict discovery response can become section authority.
  await apiClient(AIRCRAFT_SECTION_LOCAL_BOOTSTRAP_PATH, { method: "POST", signal });
}
